/**
 * Exposes the `$`-prefixed store shortcuts declared in `mappedStates` / `mappedGetters` as getters on
 * `app.config.globalProperties`, instead of installing them with `app.mixin`.
 *
 * A global mixin creates one `ComputedRefImpl` (plus its `Dep`, bound getter and accessor pair, ~280 B)
 * per entry on every component instance, Vuetify internals included. A `globalProperties` getter is
 * defined once for the whole app, and the public instance proxy falls back to it for any key the
 * instance does not own, so `this.$selectedWarehouse` and `$selectedWarehouse` in templates resolve
 * exactly as before.
 *
 * Reactivity is preserved: the getter reads the store inside whichever effect is running (a render,
 * a component computed or an Options API watcher keyed on the name), so that effect tracks the
 * dependency. Only the per-instance memoization is lost, which is worthless for plain state reads and
 * redundant for Vuex getters, which are already cached by the store.
 *
 * The functions produced by `mapState` / `mapGetters` only read `this.$store`, so they are invoked
 * with a context that carries just the store.
 */
export default {
  /**
   * @param {import('vue').App} app
   * @param {{ store: import('vuex').Store<unknown>, mixins: Array<{ computed: Record<string, () => unknown> }> }} options
   */
  install(app, { store, mixins }) {
    const storeContext = { $store: store };

    for (const mixin of mixins) {
      for (const [key, read] of Object.entries(mixin.computed)) {
        Object.defineProperty(app.config.globalProperties, key, {
          get: () => read.call(storeContext),
          configurable: true,
          enumerable: true
        });
      }
    }
  }
};
